import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, X, Minus, MagnifyingGlass, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function POSSalesScreen() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const canEditComodin = user?.role === "admin" || user?.role === "auxiliar";
  const barcodeRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState({ company_name: "Publico General", contact_name: "Publico General", rfc: "XAXX010101000" });
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [showProductList, setShowProductList] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [quotationSearch, setQuotationSearch] = useState("");
  const [showQuotationList, setShowQuotationList] = useState(false);
  const [showNewQuotation, setShowNewQuotation] = useState(false);
  const [savingQuotation, setSavingQuotation] = useState(false);
  const [quotForm, setQuotForm] = useState({ client_name: "", client_company: "", phone: "", email: "", rfc: "XAXX010101000", validity: 15, notes: "" });
  const [paymentModal, setPaymentModal] = useState(null); // {sale, mpUrl, publicUrl}

  // Payments
  const [payments, setPayments] = useState([]);
  const [newPayForm, setNewPayForm] = useState({ method: "", amount: "", reference: "" });

  // Bottom
  const [fiscalAddress, setFiscalAddress] = useState("");
  const [comments, setComments] = useState("");
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("%");
  const [ivaRate, setIvaRate] = useState(16);
  const [saving, setSaving] = useState(false);
  const [cashRegister, setCashRegister] = useState(null);
  const [posConfig, setPosConfig] = useState(null);
  const [creditDays, setCreditDays] = useState(0); // 0 = no credit
  const [creditBlock, setCreditBlock] = useState(null); // {reason, pendingSaleData}

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, contactsRes, pmRes, regRes, cfgRes, quotRes] = await Promise.all([
        axios.get(`${API}/products?limit=5000`, { headers }),
        axios.get(`${API}/comercial`, { headers }),
        axios.get(`${API}/pos/payment-methods`, { headers }),
        axios.get(`${API}/pos/cash-register/active`, { headers }),
        axios.get(`${API}/pos/config`, { headers }),
        axios.get(`${API}/pos/quotations-available`, { headers }).catch(() => ({ data: [] })),
      ]);
      setProducts(prodRes.data?.products || []);
      setContacts(contactsRes.data || []);
      setPaymentMethods(pmRes.data || []);
      if (regRes.data?.status === "open") setCashRegister(regRes.data);
      if (pmRes.data?.[0]) setNewPayForm(f => ({ ...f, method: pmRes.data[0].id }));
      setPosConfig(cfgRes.data || null);
      setQuotations(quotRes.data || []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { barcodeRef.current?.focus(); }, []);

  // Product search
  const filteredProducts = productSearch.trim()
    ? products.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase()) || p.sku?.toLowerCase().includes(productSearch.toLowerCase())).slice(0, 10)
    : [];

  const filteredCustomers = customerSearch.trim()
    ? contacts.filter(c => (c.company_name || "").toLowerCase().includes(customerSearch.toLowerCase()) || (c.contact_name || "").toLowerCase().includes(customerSearch.toLowerCase()) || (c.phone || "").includes(customerSearch)).slice(0, 8)
    : [];

  const addProduct = (product) => {
    const existing = items.find(i => i.product_id === product.id);
    if (existing) {
      setItems(items.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setItems([...items, {
        id: Date.now().toString(), product_id: product.id, sku: product.sku, name: product.name,
        item_type: product.item_type || "producto", quantity: 1, unit_price: product.precio_lista || 0, stock_qty: product.stock_qty || 0,
      }]);
    }
    setProductSearch("");
    setShowProductList(false);
    barcodeRef.current?.focus();
  };

  const handleBarcodeEnter = (e) => {
    if (e.key === "Enter" && productSearch.trim()) {
      const found = products.find(p => p.sku?.toLowerCase() === productSearch.trim().toLowerCase());
      if (found) addProduct(found);
      else { setShowProductList(true); }
    }
  };

  // Import quotation items
  const filteredQuotations = quotationSearch.trim()
    ? quotations.filter(q => (q.quotation_number || "").toLowerCase().includes(quotationSearch.toLowerCase()) || (q.client_company || q.client_name || "").toLowerCase().includes(quotationSearch.toLowerCase())).slice(0, 8)
    : quotations.slice(0, 8);

  const importQuotation = (q) => {
    // Set customer from quotation
    const contact = contacts.find(c => (c.company_name || "").toLowerCase() === (q.client_company || "").toLowerCase() || (c.contact_name || "").toLowerCase() === (q.client_name || "").toLowerCase());
    if (contact) setCustomer(contact);
    else if (q.client_company || q.client_name) {
      setCustomer({ company_name: q.client_company || q.client_name, contact_name: q.client_name, rfc: q.client_rfc || "XAXX010101000" });
    }

    // Import items
    const newItems = (q.items || []).map((item, idx) => ({
      id: `q-${Date.now()}-${idx}`,
      product_id: item.product_id || null,
      sku: item.sku || item.product_code || "",
      name: item.product_name || item.description || "",
      item_type: item.item_type || "producto",
      quantity: item.quantity || 1,
      unit_price: item.unit_price_mxn || item.unit_price || 0,
      stock_qty: 0,
    }));
    setItems(newItems);
    setQuotationSearch("");
    setShowQuotationList(false);
    toast.success(`Cotizacion ${q.quotation_number} importada con ${newItems.length} partidas`);
  };

  const openQuotationModal = () => {
    if (items.length === 0) { toast.error("Agrega productos antes de crear la cotizacion"); return; }
    setQuotForm({
      client_name: customer?.contact_name || customer?.company_name || "Publico General",
      client_company: customer?.company_name || "",
      phone: customer?.phone || "",
      email: customer?.email || "",
      rfc: customer?.rfc || "XAXX010101000",
      validity: 15,
      notes: comments,
    });
    setShowNewQuotation(true);
  };

  const handleCreateQuotation = async () => {
    if (items.length === 0) { toast.error("Agrega productos"); return; }
    setSavingQuotation(true);
    try {
      const res = await axios.post(`${API}/pos/quick-quotation`, {
        client_name: quotForm.client_name,
        client_company: quotForm.client_company || quotForm.client_name,
        client_phone: quotForm.phone,
        client_email: quotForm.email,
        client_rfc: quotForm.rfc,
        validity_days: quotForm.validity,
        items: items.map(i => ({ product_id: i.product_id, sku: i.sku, name: i.name, quantity: i.quantity, unit_price: i.unit_price })),
        notes: quotForm.notes,
      }, { headers });
      toast.success(`Cotizacion ${res.data.quotation_number} creada por $${(res.data.total_mxn || 0).toLocaleString("es-MX")}`);
      setShowNewQuotation(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear cotizacion"); }
    setSavingQuotation(false);
  };

  // Calculations
  const subtotal = items.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0);
  const discountAmount = discountType === "%" ? subtotal * (globalDiscount / 100) : globalDiscount;
  const subtotalAfterDiscount = Math.max(subtotal - discountAmount, 0);
  const taxTotal = subtotalAfterDiscount * (ivaRate / 100);
  const total = subtotalAfterDiscount + taxTotal;
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const change = Math.max(totalPaid - total, 0);
  const round = (n) => Math.round(n * 100) / 100;

  const addPayment = () => {
    const amt = parseFloat(newPayForm.amount) || 0;
    if (amt <= 0) { toast.error("Ingresa un importe"); return; }
    if (!newPayForm.method) { toast.error("Selecciona forma de pago"); return; }
    setPayments([...payments, { method: newPayForm.method, amount: round(amt), reference: newPayForm.reference }]);
    setNewPayForm({ method: newPayForm.method, amount: "", reference: "" });
  };

  const handleSave = async (docType, overrideJustification, forceCreditDays) => {
    if (items.length === 0) { toast.error("Agrega al menos un producto"); return; }
    const finalPayments = totalPaid >= total ? payments : [...payments, { method: newPayForm.method || payments[0]?.method || "efectivo", amount: round(total - totalPaid), reference: "" }];
    const useCreditDays = forceCreditDays !== undefined ? forceCreditDays : creditDays;

    setSaving(true);
    try {
      const salePayload = {
        doc_type: docType,
        customer_id: customer?.id || null,
        customer_name: customer?.company_name || customer?.contact_name || "Publico General",
        customer_rfc: customer?.rfc || "XAXX010101000",
        branch_id: "matriz", warehouse_id: "central",
        items: items.map(i => ({ product_id: i.product_id, sku: i.sku, name: i.name, item_type: i.item_type, quantity: i.quantity, unit_price: i.unit_price, discount_pct: 0, tax_rate: ivaRate })),
        global_discount_pct: discountType === "%" ? globalDiscount : 0,
        payments: finalPayments, notes: comments,
        ...(useCreditDays > 0 ? { credit_days: useCreditDays } : {}),
        ...(overrideJustification ? { credit_override: true } : {}),
      };
      const res = await axios.post(`${API}/pos/sales`, salePayload, { headers });

      // If override was used, log it
      if (overrideJustification && customer?.id) {
        await axios.post(`${API}/cxc/credit-override`, {
          customer_id: customer.id,
          sale_id: res.data.id,
          justification: overrideJustification,
        }, { headers }).catch(() => {});
      }

      toast.success(`Prefactura ${res.data.folio} creada${useCreditDays > 0 ? ` (Credito ${useCreditDays} dias)` : ""}`);

      // If payment includes card, generate MP link and show payment modal
      const hasCard = finalPayments.some(p => ["tarjeta", "tarjeta_credito", "tarjeta_debito"].includes(p.method));
      if (hasCard && res.data.status === "pendiente") {
        try {
          const mpRes = await axios.post(`${API}/payments/mercadopago/create/${res.data.id}`);
          const shareRes = await axios.get(`${API}/pos/sales/${res.data.id}/share`, { headers });
          setPaymentModal({
            sale: res.data,
            mpUrl: mpRes.data.init_point,
            publicUrl: shareRes.data.public_url,
            whatsappUrl: shareRes.data.whatsapp_url,
          });
        } catch (e) {
          console.error("MP error:", e);
          toast.error("Venta creada pero error al generar link de Mercado Pago");
        }
      }

      setItems([]); setCustomer({ company_name: "Publico General", contact_name: "Publico General", rfc: "XAXX010101000" }); setPayments([]); setGlobalDiscount(0); setComments(""); setFiscalAddress(""); setCreditDays(0);
      setCreditBlock(null);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.startsWith("CREDIT_BLOCKED:")) {
        const reason = detail.replace("CREDIT_BLOCKED:", "");
        setCreditBlock({ reason });
      } else {
        toast.error(detail || "Error al guardar");
      }
    }
    setSaving(false);
  };

  const methodName = (id) => paymentMethods.find(m => m.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="pos-sales-screen">
      {/* ===== HEADER: Empresa + Cotización + Cliente ===== */}
      <div className="bg-white border rounded-xl p-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">{posConfig?.company_name || "MAXITICKET MEXICO S.A. DE C.V."}</h2>
          <p className="text-xs text-slate-500">RFC: {posConfig?.rfc || "MTM180420H84"} | {posConfig?.address || ""}</p>
          <p className="text-xs text-slate-500">C.P. {posConfig?.postal_code || ""} | Regimen: {posConfig?.tax_regime || "601 - General de Ley"}</p>
        </div>
        <div className="w-72 shrink-0 space-y-2">
          {/* Cotización (arriba) - importar o crear */}
          <div className="relative">
            <label className="text-[10px] font-semibold text-purple-500 uppercase">Cotizacion:</label>
            <div className="flex gap-1.5 mt-1">
              <div className="relative flex-1">
                <FileText size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-purple-400" />
                <input
                  value={quotationSearch}
                  onChange={e => { setQuotationSearch(e.target.value); setShowQuotationList(true); }}
                  onFocus={() => setShowQuotationList(true)}
                  placeholder="Importar existente..."
                  className="w-full h-9 pl-8 border-2 border-dashed border-purple-300 rounded-lg text-sm focus:outline-none focus:border-purple-500 bg-white"
                  data-testid="pos-quotation-search"
                />
              </div>
              <button onClick={openQuotationModal} disabled={savingQuotation || items.length === 0}
                className="h-9 px-3 text-[10px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 shrink-0"
                data-testid="pos-create-quotation" title="Crear cotizacion con los items actuales">
                {savingQuotation ? "..." : "+ Crear"}
              </button>
            </div>
            {showQuotationList && (
              <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-56 overflow-y-auto">
                {filteredQuotations.length === 0 ? (
                  <p className="text-xs text-slate-400 p-3 text-center">Sin cotizaciones</p>
                ) : filteredQuotations.map(q => (
                  <button key={q.id} onClick={() => importQuotation(q)}
                    className="w-full text-left px-4 py-2.5 hover:bg-purple-50 text-xs border-b last:border-0 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{q.quotation_number} - {q.client_company || q.client_name}</p>
                      <p className="text-[10px] text-slate-500">{(q.items || []).length} partidas | {q.status}</p>
                    </div>
                    <span className="font-bold text-purple-700 font-mono">${(q.total_mxn || 0).toLocaleString("es-MX")}</span>
                  </button>
                ))}
              </div>
            )}
            {showQuotationList && <div className="fixed inset-0 z-10" onClick={() => setShowQuotationList(false)} />}
          </div>

          {/* Cliente (abajo) */}
          <div className="relative">
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Cliente:</label>
            <div className="relative mt-1">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={customer ? (customer.company_name || customer.contact_name) : customerSearch}
                onChange={e => { setCustomerSearch(e.target.value); setCustomer(null); setShowCustomerList(true); }}
                onFocus={() => customerSearch && setShowCustomerList(true)}
                placeholder="Publico General"
                className="w-full h-9 pl-8 pr-8 border-2 border-dashed border-blue-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white"
                data-testid="pos-customer-search"
              />
              {customer && customer.company_name !== "Publico General" && <button onClick={() => { setCustomer({ company_name: "Publico General", contact_name: "Publico General", rfc: "XAXX010101000" }); setCustomerSearch(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"><X size={14} /></button>}
            </div>
            {showCustomerList && filteredCustomers.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredCustomers.map(c => (
                  <button key={c.id} onClick={() => { setCustomer(c); setCustomerSearch(""); setShowCustomerList(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs border-b last:border-0">
                    <p className="font-semibold text-slate-800">{c.company_name || c.contact_name}</p>
                    <p className="text-[10px] text-slate-500">{c.phone} {c.email}</p>
                  </button>
                ))}
              </div>
            )}
            {showCustomerList && <div className="fixed inset-0 z-10" onClick={() => setShowCustomerList(false)} />}
          </div>
        </div>
      </div>

      {/* ===== ITEMS TABLE ===== */}
      <div className="bg-white border rounded-xl overflow-visible">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-white">
              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 w-28">Codigo</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700">Concepto</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-blue-700 w-24">Cant. ({items.reduce((s, i) => s + i.quantity, 0)})</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-blue-700 w-28">Precio</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-blue-700 w-32">Importe</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.id} className="border-b hover:bg-slate-50">
                <td className="px-4 py-2 text-xs font-mono text-slate-700">{item.sku}</td>
                <td className="px-4 py-2">
                  {item.sku === "COMODIN" && canEditComodin ? (
                    <input type="text" value={item.name} onChange={e => setItems(items.map(i => i.id === item.id ? { ...i, name: e.target.value } : i))}
                      className="w-full h-6 text-xs font-semibold border rounded px-1 text-slate-800" placeholder="Nombre del producto" data-testid={`item-name-${idx}`} />
                  ) : (
                    <span className="text-xs font-semibold text-slate-800">{item.name}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setItems(items.map(i => i.id === item.id ? { ...i, quantity: Math.max(1, i.quantity - 1) } : i))} className="w-5 h-5 rounded bg-slate-200 hover:bg-slate-300 flex items-center justify-center"><Minus size={10} /></button>
                    <input type="number" value={item.quantity} onChange={e => setItems(items.map(i => i.id === item.id ? { ...i, quantity: Math.max(1, parseInt(e.target.value) || 1) } : i))}
                      className="w-12 h-6 text-center text-xs font-mono border rounded" />
                    <button onClick={() => setItems(items.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i))} className="w-5 h-5 rounded bg-slate-200 hover:bg-slate-300 flex items-center justify-center"><Plus size={10} /></button>
                  </div>
                </td>
                <td className="px-4 py-2 text-right">
                  {isAdmin ? (
                    <input type="number" value={item.unit_price} onChange={e => setItems(items.map(i => i.id === item.id ? { ...i, unit_price: parseFloat(e.target.value) || 0 } : i))}
                      className="w-24 h-6 text-right text-xs font-mono border rounded px-1" data-testid={`item-price-${idx}`} />
                  ) : (
                    <span className="text-xs font-mono font-semibold text-slate-900">${(item.unit_price || 0).toLocaleString("es-MX")}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">${round(item.quantity * item.unit_price).toLocaleString("es-MX")}</td>
                <td className="px-4 py-2"><button onClick={() => setItems(items.filter(i => i.id !== item.id))} className="text-slate-300 hover:text-red-500"><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add product button */}
        <div className="p-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input ref={barcodeRef} value={productSearch}
              onChange={e => { setProductSearch(e.target.value); setShowProductList(true); }}
              onKeyDown={handleBarcodeEnter}
              onFocus={() => productSearch && setShowProductList(true)}
              placeholder="Buscar por nombre de producto..."
              className="w-full h-11 pl-9 border-2 border-dashed border-red-300 rounded-lg text-sm text-center text-slate-600 focus:outline-none focus:border-blue-500 hover:border-blue-400 transition-colors"
              data-testid="pos-product-search"
            />
            {showProductList && productSearch.trim() && <div className="fixed inset-0 z-10" onClick={() => setShowProductList(false)} />}
            {showProductList && productSearch.trim() && (
              <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-xl max-h-56 overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <p className="text-xs text-slate-400 p-3 text-center">Sin resultados para "{productSearch}"</p>
                ) : filteredProducts.map(p => (
                  <button key={p.id} onClick={() => addProduct(p)}
                    data-testid={`pos-product-item-${p.id}`}
                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-xs border-b last:border-0 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{p.name}</p>
                      <p className="text-[10px] text-slate-500 font-mono">{p.sku} · Stock: {p.stock_qty || 0}</p>
                    </div>
                    <span className="font-bold text-slate-900 font-mono">${(p.precio_lista || 0).toLocaleString("es-MX")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== PAGOS SECTION ===== */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="bg-blue-600 px-4 py-2.5">
          <h3 className="text-white text-sm font-bold">Pagos</h3>
        </div>
        <div className="p-4 space-y-3">
          {/* Added payments */}
          {payments.map((p, i) => (
            <div key={i} className="flex items-center gap-3 bg-slate-50 px-3 py-2 rounded-lg">
              <span className="text-xs font-semibold text-slate-700 w-36">{methodName(p.method)}</span>
              <span className="text-xs font-mono font-bold text-slate-900 w-28">${p.amount.toLocaleString("es-MX")}</span>
              <span className="text-xs text-slate-500 flex-1">{p.reference}</span>
              <button onClick={() => setPayments(payments.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
            </div>
          ))}

          {/* New payment row */}
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Forma de Pago</label>
              <select value={newPayForm.method} onChange={e => setNewPayForm({ ...newPayForm, method: e.target.value })} className="field-select mt-0.5" data-testid="pos-pay-method">
                {paymentMethods.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Importe</label>
              <input type="number" value={newPayForm.amount} onChange={e => setNewPayForm({ ...newPayForm, amount: e.target.value })}
                placeholder={`$${round(Math.max(total - totalPaid, 0)).toLocaleString("es-MX")}`}
                className="field-input font-mono mt-0.5" data-testid="pos-pay-amount"
                onKeyDown={e => e.key === "Enter" && addPayment()} />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-500 uppercase">Referencia</label>
              <input value={newPayForm.reference} onChange={e => setNewPayForm({ ...newPayForm, reference: e.target.value })} placeholder="No. referencia" className="field-input mt-0.5" />
            </div>
            <Button className="h-9 px-6 text-xs font-bold bg-blue-600 hover:bg-blue-700" onClick={addPayment} data-testid="pos-add-payment">
              Agregar
            </Button>
          </div>

          {/* Payment total */}
          <div className="text-right pt-2 border-t">
            <span className="text-sm text-slate-600">Total pagado: </span>
            <span className="text-sm font-mono font-bold text-slate-900">${round(totalPaid).toLocaleString("es-MX")}</span>
            {change > 0 && (
              <span className="ml-4 text-sm font-bold text-emerald-600">Cambio: ${round(change).toLocaleString("es-MX")}</span>
            )}
          </div>
        </div>
      </div>

      {/* ===== BOTTOM: Address/Comments + Totals ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        {/* Left */}
        <div className="bg-white border rounded-xl p-4 space-y-3">
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Direccion Fiscal</label>
            <input value={fiscalAddress} onChange={e => setFiscalAddress(e.target.value)} placeholder="Direccion fiscal del cliente..." className="field-input mt-0.5" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Comentarios</label>
            <Textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Notas, instrucciones especiales..." className="text-sm min-h-[60px] mt-0.5" />
          </div>
        </div>

        {/* Right: Totals */}
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal:</span><span className="font-mono font-semibold">${round(subtotal).toLocaleString("es-MX")}</span></div>
          <div className="flex justify-between text-sm items-center">
            <div className="flex items-center gap-1">
              <span className="text-slate-600">Descuento</span>
              <select value={discountType} onChange={e => setDiscountType(e.target.value)} className="h-6 text-xs border rounded px-1">
                <option value="%">%</option>
                <option value="$">$</option>
              </select>
              <span className="text-slate-600">:</span>
            </div>
            <input type="number" value={globalDiscount} onChange={e => setGlobalDiscount(parseFloat(e.target.value) || 0)} className="w-24 h-6 text-right font-mono text-sm border rounded px-1" data-testid="pos-global-discount" />
          </div>
          {discountAmount > 0 && <div className="flex justify-between text-xs text-red-600"><span>Descuento aplicado:</span><span className="font-mono">-${round(discountAmount).toLocaleString("es-MX")}</span></div>}
          <div className="flex justify-between text-sm items-center">
            <div className="flex items-center gap-1">
              <span className="text-slate-600">I.V.A.</span>
              <select value={ivaRate} onChange={e => setIvaRate(parseFloat(e.target.value))} className="h-6 text-xs border rounded px-1">
                <option value={16}>16%</option>
                <option value={0}>0%</option>
                <option value={8}>8%</option>
              </select>
              <span className="text-slate-600">:</span>
            </div>
            <span className="font-mono font-semibold">${round(taxTotal).toLocaleString("es-MX")}</span>
          </div>
          <div className="border-t pt-2 flex justify-between">
            <span className="text-lg font-bold text-slate-900">Total (MXN):</span>
            <span className="text-2xl font-extrabold text-slate-900 font-mono" data-testid="pos-total">${round(total).toLocaleString("es-MX")}</span>
          </div>

          {/* Action buttons */}
          <div className="space-y-2 pt-3 border-t">
            <Button className="w-full h-10 text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setCreditDays(0); handleSave("prefactura"); }} disabled={saving || items.length === 0} data-testid="pos-btn-prefactura">
              {saving && creditDays === 0 ? "Guardando..." : "Prefactura"}
            </Button>
            {/* Credit sale - only show if customer has credit authorized */}
            {customer?.id && customer?.credit_enabled && !customer?.credit_blocked && (
              <div className="flex gap-2">
                <div className="h-9 border rounded-lg px-3 flex items-center text-xs font-semibold text-amber-700 bg-amber-50 shrink-0" data-testid="pos-credit-days-display">
                  {customer.credit_days || 30} dias
                </div>
                <Button
                  className="flex-1 h-9 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => handleSave("prefactura", undefined, customer.credit_days || 30)}
                  disabled={saving || items.length === 0}
                  data-testid="pos-btn-credito"
                >
                  {saving && creditDays > 0 ? "Guardando..." : `Venta a Credito (${customer.credit_days || 30}d)`}
                </Button>
              </div>
            )}
            {customer?.id && customer?.credit_blocked && (
              <p className="text-[10px] text-red-500 font-semibold text-center">Cliente bloqueado - Sin credito disponible</p>
            )}
          </div>
        </div>
      </div>

      {/* Cash register indicator */}
      <div className={`text-center py-1.5 rounded-lg text-xs font-semibold ${cashRegister ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
        Caja: {cashRegister ? "Abierta" : "Cerrada"}
      </div>

      {/* Quotation Quick Create Modal */}
      {showNewQuotation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="bg-purple-600 p-4 text-white text-center rounded-t-2xl">
              <p className="text-sm font-bold uppercase tracking-wider">Crear Cotizacion Rapida</p>
            </div>
            <div className="p-5 space-y-4">
              {/* Client fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Empresa / Cliente *</label>
                  <input value={quotForm.client_name} onChange={e => setQuotForm({ ...quotForm, client_name: e.target.value, client_company: e.target.value })}
                    className="w-full h-9 mt-0.5 px-3 border rounded-lg text-sm" placeholder="Nombre del cliente" data-testid="quot-client-name" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">RFC</label>
                  <input value={quotForm.rfc} onChange={e => setQuotForm({ ...quotForm, rfc: e.target.value })}
                    className="w-full h-9 mt-0.5 px-3 border rounded-lg text-sm font-mono" placeholder="XAXX010101000" data-testid="quot-rfc" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Telefono</label>
                  <input value={quotForm.phone} onChange={e => setQuotForm({ ...quotForm, phone: e.target.value })}
                    className="w-full h-9 mt-0.5 px-3 border rounded-lg text-sm" placeholder="668-123-4567" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Email</label>
                  <input value={quotForm.email} onChange={e => setQuotForm({ ...quotForm, email: e.target.value })}
                    className="w-full h-9 mt-0.5 px-3 border rounded-lg text-sm" placeholder="cliente@email.com" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Validez (dias)</label>
                  <input type="number" value={quotForm.validity} onChange={e => setQuotForm({ ...quotForm, validity: parseInt(e.target.value) || 15 })}
                    className="w-full h-9 mt-0.5 px-3 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">Notas</label>
                  <input value={quotForm.notes} onChange={e => setQuotForm({ ...quotForm, notes: e.target.value })}
                    className="w-full h-9 mt-0.5 px-3 border rounded-lg text-sm" placeholder="Observaciones..." />
                </div>
              </div>

              {/* Items preview */}
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Productos ({items.length})</p>
                <div className="border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-purple-50 text-purple-700">
                        <th className="px-2 py-1.5 text-left font-semibold">Producto</th>
                        <th className="px-2 py-1.5 text-center font-semibold w-12">Cant</th>
                        <th className="px-2 py-1.5 text-right font-semibold w-20">Precio</th>
                        <th className="px-2 py-1.5 text-right font-semibold w-20">Importe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1.5 text-slate-800">{item.name}</td>
                          <td className="px-2 py-1.5 text-center font-mono">{item.quantity}</td>
                          <td className="px-2 py-1.5 text-right font-mono">${(item.unit_price || 0).toLocaleString("es-MX")}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-semibold">${(item.quantity * item.unit_price).toLocaleString("es-MX")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="text-right space-y-1 text-sm">
                <div className="flex justify-end gap-6"><span className="text-slate-500">Subtotal:</span><span className="font-mono font-semibold w-24 text-right">${round(subtotal).toLocaleString("es-MX")}</span></div>
                <div className="flex justify-end gap-6"><span className="text-slate-500">IVA 16%:</span><span className="font-mono font-semibold w-24 text-right">${round(taxTotal).toLocaleString("es-MX")}</span></div>
                <div className="flex justify-end gap-6 text-lg font-bold"><span>Total:</span><span className="font-mono w-24 text-right">${round(total).toLocaleString("es-MX")}</span></div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 flex gap-3">
              <button onClick={() => setShowNewQuotation(false)} className="flex-1 h-10 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleCreateQuotation} disabled={savingQuotation || !quotForm.client_name.trim()}
                className="flex-1 h-10 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-bold disabled:opacity-40"
                data-testid="quot-save-btn">
                {savingQuotation ? "Creando..." : "Crear Cotizacion"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal - QR + WhatsApp + Link */}
      {paymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-blue-600 p-5 text-white text-center">
              <p className="text-xs uppercase tracking-wider opacity-80">Cobro con Mercado Pago</p>
              <p className="text-3xl font-extrabold font-mono mt-1">${(paymentModal.sale.total || 0).toLocaleString("es-MX")}</p>
              <p className="text-sm opacity-80 mt-1">{paymentModal.sale.folio} - {paymentModal.sale.customer_name}</p>
            </div>
            <div className="p-6 space-y-4">
              {/* QR Code */}
              <div className="text-center">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Escanear QR para pagar</p>
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(paymentModal.mpUrl)}`} alt="QR Pago" className="mx-auto w-48 h-48 border-4 border-slate-100 rounded-xl" data-testid="payment-qr" />
              </div>
              {/* Action buttons */}
              <div className="space-y-2">
                <a href={paymentModal.whatsappUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-10 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-bold" data-testid="payment-whatsapp">
                  Enviar por WhatsApp
                </a>
                <button onClick={() => { navigator.clipboard.writeText(paymentModal.publicUrl); toast.success("Link copiado"); }}
                  className="flex items-center justify-center gap-2 w-full h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium" data-testid="payment-copy-link">
                  Copiar Link de Pago
                </button>
                <a href={paymentModal.mpUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-10 border-2 border-blue-500 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-bold" data-testid="payment-open-mp">
                  Abrir Mercado Pago
                </a>
              </div>
            </div>
            <div className="px-6 pb-5">
              <button onClick={() => setPaymentModal(null)} className="w-full h-9 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium" data-testid="payment-modal-close">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Block Modal */}
      {creditBlock && (
        <CreditBlockModal
          reason={creditBlock.reason}
          userRole={user?.role}
          onOverride={(justification) => { setCreditBlock(null); handleSave("prefactura", justification, customer?.credit_days || 30); }}
          onClose={() => setCreditBlock(null)}
        />
      )}

      <style>{`
        .field-select { width: 100%; height: 32px; padding: 0 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 12px; background: white; }
        .field-input { width: 100%; height: 32px; padding: 0 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 12px; }
        .field-select:focus, .field-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
      `}</style>
    </div>
  );
}


function CreditBlockModal({ reason, userRole, onOverride, onClose }) {
  const [justification, setJustification] = useState("");
  const canOverride = userRole === "admin" || userRole === "subgerente";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="credit-block-modal">
        <div className="bg-red-600 p-5 text-center rounded-t-2xl">
          <svg className="mx-auto mb-2 w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <p className="text-white font-bold text-lg">Credito Bloqueado</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm font-bold text-red-800 mb-1">Razon del bloqueo:</p>
            <p className="text-sm text-red-700">{reason}</p>
          </div>

          {canOverride ? (
            <div className="space-y-3 border-t pt-4">
              <p className="text-xs text-slate-500 font-semibold">Como gerente/admin, puedes autorizar esta excepcion:</p>
              <textarea
                value={justification}
                onChange={e => setJustification(e.target.value)}
                placeholder="Justificacion para la excepcion..."
                className="w-full h-20 border rounded-lg px-3 py-2 text-sm resize-none"
                data-testid="override-justification"
              />
              <div className="flex gap-2">
                <button onClick={onClose} className="flex-1 h-9 border rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
                <button
                  onClick={() => justification.trim() && onOverride(justification)}
                  disabled={!justification.trim()}
                  className="flex-1 h-9 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-bold disabled:opacity-40"
                  data-testid="override-confirm-btn"
                >
                  Autorizar Excepcion
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 border-t pt-4">
              <p className="text-xs text-slate-500">Contacta a un gerente o administrador para autorizar esta venta a credito.</p>
              <button onClick={onClose} className="w-full h-9 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-medium" data-testid="credit-block-close">
                Entendido
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
